import { cn, skeletonDelay, useDelayed } from './util.ts'

export interface SkeletonProps {
  className?: string
  /** Milliseconds before it shows (default: the 300 ms token). Pass 0 for a static design hint. */
  delay?: number
}

/** A loading placeholder block that appears only after 300 ms: <Skeleton className="h-4 w-40" />. */
export function Skeleton({ className, delay }: SkeletonProps) {
  const ready = useDelayed(delay ?? skeletonDelay())
  if (!ready) return null
  // The default corner applies only when the caller gives none (cn does not resolve conflicting classes).
  return <span aria-hidden className={cn('skeleton block', !className?.includes('rounded-') && 'rounded-sm', className)} />
}

const ROW_WIDTHS = ['w-2/5', 'w-1/3', 'w-1/4', 'w-2/5', 'w-1/3', 'w-1/5']

/** Skeleton rows for a list or table: <SkeletonRows rows={8} />. */
export function SkeletonRows({ rows = 6, delay, className }: { rows?: number; delay?: number; className?: string }) {
  const ready = useDelayed(delay ?? skeletonDelay())
  if (!ready) return null
  return (
    <div aria-hidden role="presentation" className={cn('flex flex-col divide-y divide-line', className)}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3 py-3">
          <span className="skeleton block size-7 rounded-full" />
          <span className={cn('skeleton block h-3 rounded-sm', ROW_WIDTHS[i % ROW_WIDTHS.length])} />
          <span className="skeleton ml-auto block h-3 w-16 rounded-sm" />
          <span className="skeleton block h-3 w-10 rounded-sm" />
        </div>
      ))}
    </div>
  )
}
